import { CompanySelect } from '@components'

import { Flex, Spacing } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useState } from 'react'
import { Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Company, FrenchCompany } from '@modules-deprecated/app/companies/types'

import { FormItemProps, useSegment } from '../../../../../../hooks'
import { CompanyInfo, CompanyInfoSchema, getCompanyInfoValidationSchema } from '../../utils'
import { AddressingLineSelector } from '../AddressingLineSelector'
import { AddressingLineDefaultProps } from '../AddressingLineSelector/types/frAddressingLinesFormats'
import { MatchedInformationCard } from './components'

export type EInvoicingKycFormType = 'auto-full' | 'auto-partial' | 'manual'

type CompanyInfoKeys = keyof CompanyInfo
type ValidationSchemaCompanyInfo = CompanyInfo & { formType: EInvoicingKycFormType }

interface AutoCompanyInformationFormProps extends AddressingLineDefaultProps {
  formItem: <Name extends Path<CompanyInfo>>(
    props: Omit<FormItemProps<CompanyInfoSchema, Name>, 'error'>,
  ) => JSX.Element
  onFormTypeSelected: (formType: EInvoicingKycFormType) => void
  reset: (values: ValidationSchemaCompanyInfo) => void
  setValue: (name: string, value: any) => void
  values: ValidationSchemaCompanyInfo
  addressingLine: string
  isFormEmpty: boolean
}

export const AutoCompanyInformationForm = ({
  isFormEmpty,
  onAddressingLineFormatChange,
  addressingLineFormat,
  addressingLine,
  formItem: FormItem,
  onFormTypeSelected,
  reset,
  setValue,
  values,
}: AutoCompanyInformationFormProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const validationSchema = getCompanyInfoValidationSchema(t)

  // We can reuse the zod schema, but we dont need to parse it
  // @ts-expect-error because it does not know we have the .shape key
  const requiredFields: CompanyInfoKeys[] = Object.keys(validationSchema._def.schema.shape)

  const [missingAutoFields, setMissingAutoFields] = useState<CompanyInfoKeys[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null)
  const [toggleManualCompanyForm, setToggleManualCompanyForm] = useState(false)

  const handleManualCompanyFormToggle = useCallback(() => {
    setToggleManualCompanyForm(true)
    onFormTypeSelected('manual')
    track('xxx - e-invoicing: Selected manual company details (Client)')
  }, [onFormTypeSelected, track])

  const resetSelection = useCallback(() => {
    reset({
      formType: 'auto-full',
      addressingLineFormat: values.addressingLineFormat,
      companyRegistrationNumber: '',
      companyName: '',
      accountHolderFirstName: '',
      accountHolderLastName: '',
      companyAddress: '',
      companyCity: '',
      companyZipCode: '',
    })

    setMissingAutoFields([])
    setSelectedCompanyId(null)
  }, [reset, values])

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value

      if (!value) {
        resetSelection()
      } else {
        setValue('companyRegistrationNumber', value)
      }
    },
    [setValue, resetSelection],
  )

  const handleSelectCompany = useCallback(
    (selectedCompanyId?: string | undefined, selectedCompany?: Partial<Company> | undefined) => {
      // hack to make typing work for French Companies without breaking the CompanySelect
      // @todo: We need to go deep into the useFetchcompanies hook
      // and the underlying fetchCompanies function to make them generic
      // I've started something but it goes really deep, so decided to take this approach
      // to speed-up release
      const company = selectedCompany as unknown as FrenchCompany

      // The registry sometimes concatenates several legal representatives into a single name,
      // separated by commas so we only strip the separator to leave a value the user can edit,
      // and always force manual review below instead of silently accepting it.
      const hasMultipleLegalRepresentatives = Boolean(
        company?.legalRepresentativeFirstName?.includes(',') || company?.legalRepresentativeLastName?.includes(','),
      )
      const stripNameSeparators = (name: string | null) =>
        name?.replaceAll(',', ' ').replaceAll(/\s+/g, ' ').trim() ?? ''

      const newValues: ValidationSchemaCompanyInfo = {
        formType: values.formType,
        addressingLineFormat: values.addressingLineFormat,
        companyLegalUnitRegistrationNumber: company?.siren,
        companyRegistrationNumber: company?.siret ?? '',
        companyName: company?.legalName ?? '',
        accountHolderFirstName: stripNameSeparators(company?.legalRepresentativeFirstName ?? null),
        accountHolderLastName: stripNameSeparators(company?.legalRepresentativeLastName ?? null),
        companyAddress: company?.street || '',
        companyCity: company?.city || '',
        companyZipCode: company?.zipcode || '',
      }

      // Reset with all new values at once
      reset(newValues)

      if (selectedCompanyId) {
        setSelectedCompanyId(selectedCompanyId)
      }

      const missingFields = requiredFields.reduce<CompanyInfoKeys[]>((missing, field) => {
        const forceManualAccountHolderReview =
          hasMultipleLegalRepresentatives && (field === 'accountHolderFirstName' || field === 'accountHolderLastName')

        // A field auto-filled from the SIRET lookup can still be invalid (e.g. unsupported
        // characters), in which case it must be surfaced as an editable field instead of
        // silently blocking submission.
        // @ts-expect-error validationSchema._def.schema.shape is not typed but is safe to access here
        const isInvalid = !validationSchema._def.schema.shape[field].safeParse(newValues[field]).success

        if (forceManualAccountHolderReview || isInvalid) {
          missing.push(field)
        }
        return missing
      }, [])

      onFormTypeSelected(missingFields.length ? 'auto-partial' : 'auto-full')

      setMissingAutoFields(missingFields)

      setToggleManualCompanyForm(false)
    },
    [
      values.addressingLineFormat,
      values.formType,
      reset,
      setSelectedCompanyId,
      setMissingAutoFields,
      requiredFields,
      onFormTypeSelected,
      validationSchema,
    ],
  )

  const getDisplayValueForCompany = useCallback(
    (item: Partial<Company>) => (item as unknown as FrenchCompany).siret ?? '',
    [],
  )

  /**
   * If no company is selected but we have previously saved company data,
   * then we display its registration number as the value
   */
  const controlledInputValue = selectedCompanyId ? undefined : values.companyRegistrationNumber

  return (
    <Flex flexDirection="column" gap={Spacing.S24}>
      <CompanySelect
        onBlur={handleInputChange}
        onChange={handleInputChange}
        onSelect={handleSelectCompany}
        selectedId={selectedCompanyId ?? undefined}
        getDisplayValue={getDisplayValueForCompany}
        inputValue={controlledInputValue}
        // @ts-expect-error until we fix the CompanyType types
        getCompanyDisplayTitle={(company) => (company as unknown as FrenchCompany).legalName}
        placeholder={t('e_invoice.kyc.step.company_details.search_input.placeholder')}
        showAddress={false}
        type="number"
        useDebouncedSearch
      />

      <MatchedInformationCard
        // we always have a value, coming from the form
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        addressingLineFormat={addressingLineFormat!}
        formItem={FormItem}
        isFormEmpty={isFormEmpty}
        values={values}
        missingAutoFields={missingAutoFields}
        toggleManualCompanyForm={toggleManualCompanyForm}
        onManualCompanyFormToggle={handleManualCompanyFormToggle}
      />
      {values.companyLegalUnitRegistrationNumber || toggleManualCompanyForm ? (
        <AddressingLineSelector
          addressingLine={addressingLine}
          onAddressingLineFormatChange={onAddressingLineFormatChange}
          addressingLineFormat={addressingLineFormat}
        />
      ) : null}
    </Flex>
  )
}
